"use client";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useStoreBuilderContext } from "@/ui/admin/store-builder/store-builder-context";
import { StoreBuilderUpload } from "@/ui/admin/store-builder/store-builder-upload";
import { debounce } from "@/ui/hooks/use-debounced-value";
import { Label } from "@/ui/shadcn/label";
import { StatefulColorPicker } from "@/ui/stateful-color-picker";
import { StatefulInput } from "@/ui/stateful-input";
import {
	AlignCenterIcon,
	AlignLeftIcon,
	AlignRightIcon,
	ChevronsLeftRightEllipsis,
	GalleryHorizontal,
	PlusCircle,
	Trash2Icon,
} from "lucide-react";
import { useState } from "react";
import type { YnsComponentForm } from "../types";
import type { HeroSection } from "./hero-section";

export const HeroSectionForm: YnsComponentForm<typeof HeroSection> = ({ data, theme }) => {
	const context = useStoreBuilderContext<typeof HeroSection>();
	const initialSlides = "slides" in data ? (data.slides ?? []) : [data];
	const [slides, setSlides] = useState(initialSlides);

	const addSlide = () => {
		const newSlides = [
			...slides,
			{
				title: "",
				description: "",
				image: { src: "", alt: "" },
				button: { label: "", path: "" },
			},
		];
		setSlides(newSlides);
		context.saveSelectedPageSectionField({
			fieldPath: "slides",
			value: newSlides,
		});
	};

	const removeSlide = (index: number) => {
		if (slides.length <= 1) return; // Keep at least one slide

		const newSlides = slides.filter((_, i) => i !== index);
		setSlides(newSlides);
		context.saveSelectedPageSectionField({
			fieldPath: "slides",
			value: newSlides,
		});
	};

	const updateSlideField = (index: number, fieldPath: string, value: string, save = true) => {
		const newSlides = [...slides];

		// Handle nested paths like "button.label"
		const pathParts = fieldPath.split(".");
		let current = newSlides[index];

		for (let i = 0; i < pathParts.length - 1; i++) {
			if (!current) return;
			const cursor = pathParts[i];
			if (!cursor) return;

			// @ts-ignore
			current = current[cursor];
		}

		// @ts-ignore
		current[pathParts[pathParts.length - 1]] = value;

		setSlides(newSlides);

		if (save) {
			context.saveSelectedPageSectionField({
				fieldPath: "slides",
				value: newSlides,
			});
		}
	};

	return (
		<div className="grid gap-4 relative pb-20">
			<Label className="grid gap-2 mt-2 w-full">
				<span>Box alignment</span>
				<Tabs
					defaultValue={data.size ?? "fixed-width"}
					className="w-full"
					onValueChange={(value) => {
						context.saveSelectedPageSectionField({
							fieldPath: "size",
							value: value,
						});
					}}
				>
					<TabsList className="grid w-full grid-cols-2">
						<TabsTrigger value="fixed-width">
							<GalleryHorizontal className="size-4 mr-1" />
							Fixed Width
						</TabsTrigger>
						<TabsTrigger value="full-width">
							<ChevronsLeftRightEllipsis className="size-4 mr-1" />
							Full Width
						</TabsTrigger>
					</TabsList>
				</Tabs>
			</Label>

			<Accordion type="multiple" className="w-full">
				{slides.map((slide, index) => (
					<AccordionItem key={index} value={`slide-${index}`}>
						<div className="flex items-center justify-between">
							<AccordionTrigger className="flex-1">{slide.title || "Untitled Slide"}</AccordionTrigger>
							<Button
								variant="ghost"
								size="sm"
								onClick={(e) => {
									e.stopPropagation();
									removeSlide(index);
								}}
								className={cn("mr-2", slides.length <= 1 && "invisible")}
								disabled={slides.length <= 1}
							>
								<Trash2Icon className="h-4 w-4" />
							</Button>
						</div>
						<AccordionContent>
							<div className="grid gap-4 pl-2 pt-4">
								<Label className="grid gap-2 mt-2 w-full">
									<span>Box alignment</span>
									<Tabs
										defaultValue={slide.boxAlignment}
										className="w-full"
										onValueChange={(value) => {
											updateSlideField(index, "boxAlignment", value);
										}}
									>
										<TabsList className="grid w-full grid-cols-3">
											<TabsTrigger value="left">
												<AlignLeftIcon className="size-4 mr-1" />
												Left
											</TabsTrigger>
											<TabsTrigger value="center">
												<AlignCenterIcon className="size-4 mr-1" />
												Center
											</TabsTrigger>
											<TabsTrigger value="right">
												<AlignRightIcon className="size-4 mr-1" />
												Right
											</TabsTrigger>
										</TabsList>
									</Tabs>
								</Label>

								<Label className="grid gap-2 mt-2 w-full">
									<span>Text alignment</span>
									<Tabs
										defaultValue={slide.textAlignment}
										className="w-full"
										onValueChange={(value) => {
											updateSlideField(index, "textAlignment", value);
										}}
									>
										<TabsList className="grid w-full grid-cols-3">
											<TabsTrigger value="left">
												<AlignLeftIcon className="size-4 mr-1" />
												Left
											</TabsTrigger>
											<TabsTrigger value="center">
												<AlignCenterIcon className="size-4 mr-1" />
												Center
											</TabsTrigger>
											<TabsTrigger value="right">
												<AlignRightIcon className="size-4 mr-1" />
												Right
											</TabsTrigger>
										</TabsList>
									</Tabs>
								</Label>

								<Label className="grid gap-2 mt-2">
									<span>Title</span>
									<StatefulInput
										required
										defaultValue={slide.title}
										onValueChange={debounce(
											(value) => {
												updateSlideField(index, "title", value);
											},
											300,
											{ trailing: true },
										)}
									/>
								</Label>
								<Label className="grid gap-2 mt-2">
									<span>Description</span>
									<StatefulInput
										required
										defaultValue={slide.description}
										onValueChange={debounce(
											(value) => {
												updateSlideField(index, "description", value);
											},
											300,
											{ trailing: true },
										)}
									/>
								</Label>
								<div className="grid gap-2 mt-2">
									<div>
										<div>Image</div>
										<div className="text-xs text-neutral-600">
											Formats: PNG, JPG, WebP, AVIF ⋅ Dimensions: 1920x1080 (16:9) ⋅ Max size: 4.5 MB{" "}
										</div>
									</div>
									<StoreBuilderUpload
										width={1920}
										height={1080}
										className=""
										fieldPathPrefix={`slides.${index}.image.`}
										images={[slide.image.src].filter(Boolean)}
										onUpload={(src) => {
											updateSlideField(index, "image.src", src, false);
										}}
										onDelete={() => {
											updateSlideField(index, "image.src", "", false);
										}}
									/>
								</div>
								<Label className="grid gap-2 mt-2">
									<span>The label of the button link</span>
									<StatefulInput
										required
										defaultValue={slide.button.label}
										onValueChange={debounce(
											(value) => {
												updateSlideField(index, "button.label", value);
											},
											300,
											{ trailing: true },
										)}
									/>
								</Label>
								<Label className="grid gap-2 mt-2">
									<span>The path of the link</span>
									<StatefulInput
										required
										defaultValue={slide.button.path}
										onValueChange={debounce(
											(value) => {
												updateSlideField(index, "button.path", value);
											},
											300,
											{ trailing: true },
										)}
									/>
								</Label>
							</div>
						</AccordionContent>
					</AccordionItem>
				))}
			</Accordion>

			<Button variant="outline" className="flex items-center gap-2 w-full mt-4" onClick={addSlide}>
				<PlusCircle className="h-4 w-4" />
				Add New Slide
			</Button>

			<div className="mt-8 pt-4 border-t">
				<h3 className="font-medium mb-4">Theme Settings</h3>
				<div className="grid gap-6 2xl:grid-cols-2">
					<Label className="grid gap-2">
						<span>Background Color</span>
						<StatefulColorPicker
							defaultValue={theme?.backgroundColor}
							onValueChange={debounce(
								(value) => {
									context.saveSelectedPageSectionThemeField({
										fieldPath: "backgroundColor",
										value,
									});
								},
								300,
								{ trailing: true },
							)}
						/>
					</Label>
					<Label className="grid gap-2">
						<span>Font Color</span>
						<StatefulColorPicker
							defaultValue={theme?.color}
							onValueChange={debounce(
								(value) => {
									context.saveSelectedPageSectionThemeField({
										fieldPath: "color",
										value,
									});
								},
								300,
								{ trailing: true },
							)}
						/>
					</Label>

					<Label className="grid gap-2">
						<span>Button Background</span>
						<StatefulColorPicker
							defaultValue={theme?.buttonBackgroundColor}
							onValueChange={debounce(
								(value) => {
									context.saveSelectedPageSectionThemeField({
										fieldPath: "buttonBackgroundColor",
										value,
									});
								},
								300,
								{ trailing: true },
							)}
						/>
					</Label>
					<Label className="grid gap-2">
						<span>Button Text Color</span>
						<StatefulColorPicker
							defaultValue={theme?.buttonTextColor}
							onValueChange={debounce(
								(value) => {
									context.saveSelectedPageSectionThemeField({
										fieldPath: "buttonTextColor",
										value,
									});
								},
								300,
								{ trailing: true },
							)}
						/>
					</Label>

					<Label className="grid gap-2">
						<span>Button Background when mouse is over</span>
						<StatefulColorPicker
							defaultValue={theme?.buttonHoverBackgroundColor}
							onValueChange={debounce(
								(value) => {
									context.saveSelectedPageSectionThemeField({
										fieldPath: "buttonHoverBackgroundColor",
										value,
									});
								},
								300,
								{ trailing: true },
							)}
						/>
					</Label>
				</div>
			</div>
		</div>
	);
};
